import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const EditComboOrderModal = ({
  editData,
  handleCloseAddComboOrderEdit,
  setResults,
}) => {
  const { id } = useParams();

  const [platformTypeData, setPlatformTypeData] = useState([]);
  const [productData, setProductData] = useState([]);

  const [platformType, setPlatformType] = useState("");
  const [profit, setProfit] = useState("");
  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState({});

  const refSubmitDis = useRef(null);

  // Fetch platform types and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [platformResponse, productResponse] =
          await Promise.all([
            axios.get(
              "https://api.velormall.com/api/admin/platform-type/all/view"
            ),
            axios.get(
              "https://api.velormall.com/api/admin/product/all/view"
            ),
          ]);

        setPlatformTypeData(
          platformResponse?.data?.data || []
        );

        setProductData(
          productResponse?.data?.data || []
        );
      } catch (error) {
        toast.error("Failed to load platforms or products");
      }
    };

    fetchData();
  }, []);

  // Populate fields from existing order
  useEffect(() => {
    if (!editData?._id) return;

    const currentPlatformId =
      editData?.platformTypeId?._id ||
      editData?.platformTypeId ||
      "";

    setPlatformType(String(currentPlatformId));
    setProfit(String(editData?.profit ?? ""));

    setProducts(
      (editData?.products || []).map((item, index) => {
        const productId =
          item?.productId?._id ||
          item?.productId ||
          "";

        return {
          rowId: `${editData._id}-${index}-${productId}`,
          productId: String(productId),
          // Preserve the price saved with this order.
          price: String(item?.price ?? ""),
          quantity: String(item?.quantity ?? 1),
        };
      })
    );

    setErrorMessage({});
  }, [editData]);

  // Add product row
  const handleAddProduct = () => {
    setProducts((prev) => [
      ...prev,
      {
        rowId: `${Date.now()}-${Math.random()}`,
        productId: "",
        price: "",
        quantity: "1",
      },
    ]);
  };

  // Remove product row
  const handleRemoveProduct = (rowId) => {
    setProducts((prev) =>
      prev.filter((item) => item.rowId !== rowId)
    );
  };

  // Change product
  const handleProductChange = (rowId, productId) => {
    const selectedProduct = productData.find(
      (item) => item._id === productId
    );

    setProducts((prev) =>
      prev.map((item) =>
        item.rowId === rowId
          ? {
            ...item,
            productId,
            price: selectedProduct
              ? String(selectedProduct.price ?? "")
              : "",
          }
          : item
      )
    );

    setErrorMessage({});
  };

  // Change quantity
  const handleQuantityChange = (rowId, quantity) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.rowId === rowId
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Calculate subtotal
  const subtotal = products.reduce((total, item) => {
    if (
      !item.productId ||
      item.price === "" ||
      item.quantity === ""
    ) {
      return total;
    }

    const price = Number(item.price);
    const quantity = Number(item.quantity);

    if (
      !Number.isFinite(price) ||
      !Number.isInteger(quantity) ||
      price < 0 ||
      quantity < 1
    ) {
      return total;
    }

    return total + price * quantity;
  }, 0);

  const validProfit =
    profit !== "" &&
    Number.isFinite(Number(profit)) &&
    Number(profit) >= 0;

  const profitAmount = validProfit
    ? (subtotal * Number(profit)) / 100
    : 0;

  const expectedIncome = validProfit
    ? subtotal + profitAmount
    : "";

  // Update combo order
  const handleSubmitData = async (event) => {
    event.preventDefault();
    setErrorMessage({});

    if (!editData?._id) {
      toast.error("Order ID not found");
      return;
    }


    if (refSubmitDis.current) {
      refSubmitDis.current.disabled = true;
    }

    try {
      const payload = {
        type: "Combo",
        orderSetId:
          editData?.orderSetId?._id ||
          editData?.orderSetId ||
          id,
        platformTypeId: platformType,
        profit: Number(profit),
        products: products.map((item) => ({
          productId: item.productId,
          quantity: Number(item.quantity),
        })),
      };

      const response = await axios.put(
        `https://api.velormall.com/api/admin/order/update/${editData._id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        setResults(response.data);
        toast.success(response?.data?.message);
        handleCloseAddComboOrderEdit();
      } else {
        setErrorMessage(response?.data?.errors || {});
        if (response?.data?.message) {
          toast.error(response?.data?.message);
        }
      }
    } catch (error) {
      const responseData = error?.response?.data;
      setErrorMessage(responseData?.errors || {});
      if (responseData?.message) {
        toast.error(responseData?.message);
      }


    } finally {
      if (refSubmitDis.current) {
        refSubmitDis.current.disabled = false;
      }
    }
  };

  return (
    <>
      <Modal.Body>
        <form onSubmit={handleSubmitData}>
          <div className="modal-body">
            {/* Platform */}
            <div className="form-group mb-3">
              <label
                htmlFor="editPlatformType"
                className="required"
              >
                Select Platform
              </label>

              <select
                className="form-control"
                id="editPlatformType"
                value={platformType}
                onChange={(e) =>
                  setPlatformType(e.target.value)
                }
              >
                <option value="">Select Platform</option>

                {platformTypeData.map((item) => (
                  <option
                    key={item._id}
                    value={item._id}
                  >
                    {item.name}
                  </option>
                ))}
              </select>

              {errorMessage?.platformTypeId?.msg && (
                <span className="text-danger">
                  {errorMessage.platformTypeId.msg}
                </span>
              )}
            </div>

            {/* Add Product and Profit */}
            <div className="row mb-4">
              <div className="col-md-6 mb-2">
                <label className="d-block">
                  &nbsp;
                </label>

                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  onClick={handleAddProduct}
                >
                  + Add Product
                </button>

                {errorMessage?.products?.msg && (
                  <span className="text-danger d-block">
                    {errorMessage.products.msg}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-2">
                <label
                  htmlFor="editProfit"
                  className="required"
                >
                  Profit %
                </label>

                <input
                  type="number"
                  className="form-control"
                  id="editProfit"
                  value={profit}
                  onChange={(e) =>
                    setProfit(e.target.value)
                  }
                  min="0"
                  step="any"
                />

                {errorMessage?.profit?.msg && (
                  <span className="text-danger">
                    {errorMessage.profit.msg}
                  </span>
                )}
              </div>
            </div>

            {/* Product rows */}
            {products.map((item, index) => (
              <div
                className="row align-items-end mb-3"
                key={item.rowId}
              >
                <div className="col-md-5 col-12 mb-2">
                  <label
                    htmlFor={`editProduct-${item.rowId}`}
                    className="required"
                  >
                    Manage Product {index + 1}
                  </label>

                  <select
                    className="form-control"
                    id={`editProduct-${item.rowId}`}
                    value={item.productId}
                    onChange={(e) =>
                      handleProductChange(
                        item.rowId,
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      Select Product
                    </option>

                    {productData.map((product) => (
                      <option
                        key={product._id}
                        value={product._id}
                      >
                        {product.name}
                      </option>
                    ))}
                  </select>

                  {errorMessage?.[
                    `products[${index}].productId`
                  ]?.msg && (
                      <span className="text-danger">
                        {
                          errorMessage[
                            `products[${index}].productId`
                          ].msg
                        }
                      </span>
                    )}
                </div>

                <div className="col-md-3 col-6 mb-2">
                  <label
                    htmlFor={`editPrice-${item.rowId}`}
                  >
                    Price
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    id={`editPrice-${item.rowId}`}
                    value={
                      item.productId
                        ? item.price
                        : ""
                    }
                    readOnly
                  />
                </div>

                <div className="col-md-3 col-5 mb-2">
                  <label
                    htmlFor={`editQuantity-${item.rowId}`}
                    className="required"
                  >
                    Quantity
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    id={`editQuantity-${item.rowId}`}
                    value={
                      item.productId
                        ? item.quantity
                        : ""
                    }
                    onChange={(e) =>
                      handleQuantityChange(
                        item.rowId,
                        e.target.value
                      )
                    }
                    min="1"
                    step="1"
                  />

                  {errorMessage?.[
                    `products[${index}].quantity`
                  ]?.msg && (
                      <span className="text-danger">
                        {
                          errorMessage[
                            `products[${index}].quantity`
                          ].msg
                        }
                      </span>
                    )}
                </div>

                <div className="col-md-1 col-1 mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    title="Remove product"
                    onClick={() =>
                      handleRemoveProduct(item.rowId)
                    }
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}

            {/* Totals */}
            <div className="row mt-4">
              <div className="col-md-6 ms-auto">
                <div className="border rounded p-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <strong>
                      ${subtotal.toFixed(2)}
                    </strong>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Profit Amount:</span>
                    <strong>
                      ${profitAmount.toFixed(2)}
                    </strong>
                  </div>

                  <div className="d-flex justify-content-between">
                    <strong>Expected Income:</strong>
                    <strong className="text-success">
                      {expectedIncome === ""
                        ? "—"
                        : `$${expectedIncome.toFixed(2)}`}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              ref={refSubmitDis}
              className="btn btn-primary w-100 mt-3"
            >
              Update Combo Order
            </button>
          </div>
        </form>
      </Modal.Body>
    </>
  );
};

export default EditComboOrderModal;